import moment from 'moment';
import { _isEmpty } from '../../utils/function.utils';
import { CacheAreaKey, CacheConfig } from '../config/cache-config';

const DEBOUNCE_KEY = '_m';

//this is our interface which has key and its type is T.
export interface Cache<T> {
  [key: string]: Record<
    string,
    {
      value: T;
      created: string;
    }
  >;
}

const DEFAULT_PREFIX = 'dstv-dvs_cnxt';


abstract class StorageBase<T> {
  // protected readonly inMemory: Cache<T> = {};
  protected readonly session: globalThis.Storage = sessionStorage;
  protected readonly local: globalThis.Storage = localStorage;
}

export class StorageHelper<T> extends StorageBase<T> {
  private inMemory: Record<string, Cache<T>> = {};
  private static instance: StorageHelper<{ [key: string]: any }>;
  private prefix: string = DEFAULT_PREFIX;

  protected get storage(): globalThis.Storage {
    return this.local || this.session;
  }

  private constructor() {
    super();
  }

  public static get GetInstance() {
    if (!this.instance) {
      this.instance = new StorageHelper();
    }

    return this.instance;
  }

  public isExpired(created?: string, expiry?: number) {
    if (!created || !expiry) {
      return false;
    }
    return moment().isSameOrAfter(moment(created).add(expiry, 'minutes'));
  }

  public getFromCache(key: string, innerKey?: string, areaKey?: CacheAreaKey): T | null | unknown {
    if (innerKey) {
      if (!this.inMemory[key]) {
        this.inMemory[key] = this.getFromStorage(key) || {};
      }
      const area = areaKey && Object.prototype.hasOwnProperty?.call(this.inMemory[key] || {}, areaKey);
      const inner = Object.prototype.hasOwnProperty?.call(area ? this.inMemory[key][areaKey] : this.inMemory[key] || {}, innerKey);

      if (!inner && !area) {
        return null;
      }

      const cacheItem = area ? this.inMemory[key][areaKey][innerKey] : null;
      const item = this.inMemory[key][innerKey];
      const isExpired = areaKey && this.isExpired(cacheItem?.created, CacheConfig[areaKey].cacheTimeout);

      return cacheItem && !isExpired ? cacheItem?.value : !area ? item : null;
    }

    if (!this.inMemory[key]) {
      this.inMemory[key] = this.getFromStorage(key);
    }
    return this.inMemory[key] || null;
  }

  public setCacheAndReturn(key: string, areaKey?: CacheAreaKey | null, value?: T, innerKey?: string): T | null {
    if (!value || _isEmpty(value)) {
      return null;
    }
    // Check debounce time (only keep in-mem when debounced)
    let shouldSave = true;
    if (areaKey) {
      const inner = Object.prototype.hasOwnProperty?.call(this.inMemory[key] || {}, CacheConfig[areaKey].key);
      if (inner && Object.prototype.hasOwnProperty?.call(this.inMemory[key][CacheConfig[areaKey].key] || {}, DEBOUNCE_KEY)) {
        const debounced = this.inMemory[key][CacheConfig[areaKey].key][DEBOUNCE_KEY];
        if (moment(debounced.created).add(CacheConfig[areaKey].debounceCacheTime, 'seconds').isAfter(moment())) {
          shouldSave = false;
        } else {
          delete this.inMemory[key][CacheConfig[areaKey].key][DEBOUNCE_KEY]
          this.saveToStorage(key);
        }

      }
    }

    this.setCache(key, value, areaKey || null, innerKey, shouldSave);
    return value;
  }

  public setCache(key: string, value: T, areaKey?: CacheAreaKey | null, innerKey?: string, shouldSave = true): void {
    if (areaKey) {
      this.inMemory[key] = this.getFromStorage(key);
      if (this.inMemory[key] && innerKey) {
        const previous = this.inMemory[key][CacheConfig[areaKey].key];
        const newItem = {
          [CacheConfig[areaKey].key]: {
            ...previous,
            [innerKey]: {
              value,
              created: moment().format(),
            },
          },
        };
        this.inMemory[key] = {
          ...this.inMemory[key],
          ...newItem,
        };
      } else if (innerKey) {
        this.inMemory[key] = {
          [CacheConfig[areaKey].key]: {
            [innerKey]: {
              value,
              created: moment().format(),
            },
          },
        };
      } else {
        this.inMemory[key] = value as any;
      }
    } else if (this.inMemory[key] && innerKey) {
      if ((this.inMemory[key] as Record<string, unknown>)[innerKey] === value) {
        return;
      }

      this.inMemory[key] = {
        ...this.inMemory[key],
        [innerKey]: value,
      } as any;
    } else if (this.inMemory[key] === value) {
      return;
    } else {
      this.inMemory[key] = value as any;
    }
    if (shouldSave) {
      this.saveToStorage(key);
    }
  }

  public removeFromStorage(key: string, innerKey?: CacheAreaKey): void {
    if (innerKey) {
      if (!this.inMemory[key]) {
        this.inMemory[key] = this.getFromStorage(key) || ({} as Cache<T>);
      }
      const inner = Object.prototype.hasOwnProperty?.call(this.inMemory[key] || {}, innerKey);

      if (!inner) {
        return;
      }

      delete (this.inMemory[key] as Record<string, unknown>)[innerKey];
      this.saveToStorage(key);
      return;
    }

    delete this.inMemory[key];
    this.storage.removeItem(`${this.prefix}_${key}`);
  }

  public debounceStorageArea(key: string, areaKey?: CacheAreaKey | null): void {
    if (areaKey) {
      if (!this.inMemory[key]) {
        this.inMemory[key] = this.getFromStorage(key) || ({} as Cache<T>);
      }

      const newItem = {
        [CacheConfig[areaKey].key]: {
          [DEBOUNCE_KEY]: {
            created: moment().toJSON(),
            debounce: CacheConfig[areaKey].debounceCacheTime,
          },
        },
      } as any;
      this.inMemory[key] = {
        ...this.inMemory[key],
        ...newItem,
      };

      this.saveToStorage(key);
      return;
    }

    delete this.inMemory[key];
    this.storage.removeItem(`${this.prefix}_${key}`);
  }

  private getFromStorage(key: string, deobfuscate = true): Cache<T> {
    let storedPackage;
    try {
      const cached = this.storage.getItem(`${this.prefix}_${key}`);
      if (!cached) {
        storedPackage = null;
      } else {
        const value = deobfuscate ? window.atob(cached) : cached;
        storedPackage = cached ? JSON.parse(value) : null;
      }

    } catch (error) {
      // fails silently.
    }
    return storedPackage || null;
  }

  private saveToStorage(key: string, obfuscate = true): void {
    try {
      const value = obfuscate ? window.btoa(JSON.stringify(this.inMemory[key])) : JSON.stringify(this.inMemory[key]);
      this.storage.setItem(`${this.prefix}_${key}`, value);
    } catch (error) {
      // fails silently.
    }
  }
}
